import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck, Lock, FileSearch } from "lucide-react";
import { ArrowCta, Overline, scrollToForm } from "./Shared";
import { scrollToId } from "@/hooks/useSmoothScroll";

const EASE = [0.16, 1, 0.3, 1];
const HERO_IMG =
  "https://images.unsplash.com/photo-1436450412740-6b988f486c6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxsYXclMjBjb3VydGhvdXNlJTIwY29sdW1ucyUyMGp1c3RpY2V8ZW58MHx8fHwxNzg3MDg3NTg5fDA&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.95, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden border-b border-[#E7E2DA] pt-28 pb-16 sm:pt-36 sm:pb-24"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-end">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Overline data-testid="hero-overline">
                Free & Confidential · No Obligation
              </Overline>
            </motion.div>

            <h1 className="mt-6 font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight text-[#1A1715]">
              <Line delay={0.2}>Harmed by a product,</Line>
              <Line delay={0.32}>a company, or an institution?</Line>
              <Line delay={0.44}>
                <span className="italic text-[#C2410C]">
                  Understand your legal options.
                </span>
              </Line>
            </h1>

            <motion.p
              data-testid="hero-subtext"
              className="mt-8 max-w-xl font-news text-lg text-[#44403C] leading-relaxed"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
            >
              We connect people affected by defective drugs, toxic exposure, and
              corporate negligence with independent legal professionals for a
              free, confidential case review. Submitting takes about two
              minutes — and commits you to nothing.
            </motion.p>

            <motion.div
              className="mt-10 flex flex-wrap items-center gap-6"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            >
              <ArrowCta testId="hero-cta-button" onClick={scrollToForm}>
                Start Your Free Case Review
              </ArrowCta>
              <button
                type="button"
                data-testid="hero-how-it-works-link"
                onClick={() => scrollToId("process")}
                className="group inline-flex items-center gap-2 font-sans font-semibold text-sm text-[#1A1715] border-b border-[#1A1715] pb-1 transition-colors duration-300 hover:text-[#C2410C] hover:border-[#C2410C]"
              >
                How it works
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </button>
            </motion.div>
          </div>

          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: EASE }}
          >
            <div
              ref={imgRef}
              className="relative overflow-hidden rounded-sm border border-[#E7E2DA] shadow-[0_24px_60px_rgba(26,23,21,0.10)]"
            >
              <motion.img
                src={HERO_IMG}
                alt="Classical courthouse columns representing civil justice"
                data-testid="hero-image"
                className="h-72 sm:h-96 lg:h-[30rem] w-full object-cover scale-110"
                style={{ y: parallaxY }}
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1715]/35 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-5 py-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FBF9F5]/90">
                  Civil Justice · Est. Intake Network
                </span>
                <span className="font-serif italic text-[#FBF9F5]/90 text-sm">
                  Ch. 01
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          data-testid="hero-trust-bar"
          className="mt-16 sm:mt-20 grid grid-cols-1 sm:grid-cols-3 border-t border-[#E7E2DA]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1 }}
        >
          {[
            { icon: FileSearch, label: "Free review of your situation" },
            { icon: Lock, label: "Confidential — your data stays protected" },
            { icon: ShieldCheck, label: "No obligation, no upfront cost" },
          ].map(({ icon: Icon, label }, i) => (
            <div
              key={label}
              className={`flex items-center gap-3 py-5 sm:py-6 ${
                i > 0 ? "border-t sm:border-t-0 sm:border-l border-[#E7E2DA] sm:pl-8" : ""
              }`}
            >
              <Icon className="h-5 w-5 text-[#B45309] shrink-0" strokeWidth={1.75} />
              <span className="font-sans text-sm font-medium text-[#44403C]">
                {label}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
